/**
 * What a filter bar can do to a list of filters, as data.
 *
 * PURE, and shared by every host of the bar. The bar never holds the filters
 * itself — it is handed them, and it hands back an ACTION saying what the
 * reader did. Where the result is kept is the host's business: the report
 * builder folds these into its spec reducer, the dashboard into the URL. The
 * rules for what an action MEANS live here once, so a filter edited over a
 * report and the same filter edited over a dashboard cannot come out
 * different.
 *
 * Actions rather than a next list, because a list computed from props is a
 * whole event stale: a control that commits a value and closes its panel on
 * one click would have its second call built from the state before its first.
 * An action is applied to whatever the host's state is by then.
 */
import {
  allowsOp,
  DEFAULT_OP,
  type FilterNode,
  type FilterOp,
  type FilterRule,
  type FilterValue,
  type FilterValueType,
  isFilterGroup,
  newFilterId,
  opArity,
} from '@clockwise/semantic-core';

export type FilterAction =
  /**
   * A new chip, at the end of the bar, with its type's default operator.
   *
   * `id`, `op` and `value` are for a chip that was already ON the bar before
   * it had a rule — a pinned one — so it keeps its identity (and its open
   * panel) as it becomes one. Each is honoured only if it is valid for the
   * field.
   */
  | { type: 'addFilter'; dimensionId: string; id?: string; op?: FilterOp; value?: FilterValue }
  | { type: 'removeFilter'; filterId: string }
  /** Change a rule's column, operator or value — see {@link settleRule} on values. */
  | { type: 'setFilterRule'; filterId: string; patch: Partial<Omit<FilterRule, 'id'>> };

/**
 * What kind of value a field takes, or null when it cannot be filtered.
 *
 * The one thing the actions need to know about the model, asked as a function
 * so a host whose fields span several models can answer it.
 */
export type FilterTypeOf = (field: string) => FilterValueType | null;

/**
 * The operator and value a rule may carry, given its field's type.
 *
 * The value is DROPPED when the operator changes to one that reads a different
 * shape, rather than coerced: a list of customer ids is not a number, and
 * guessing produces a filter the reader did not write. A change that keeps the
 * shape keeps the value, which is what makes "≥ 40" → "between 40 – …" the
 * cheap edit it looks like.
 */
function settleRule(rule: FilterRule, type: FilterValueType): FilterRule {
  const next = { ...rule };
  if (!allowsOp(type, next.op)) next.op = DEFAULT_OP[type];
  const arity = opArity(next.op, type);
  if (arity === 'none') next.value = undefined;
  else if (next.value && next.value.kind !== arity) next.value = undefined;
  return next;
}

/**
 * Apply one action. Returns the SAME array when nothing changed, so a host can
 * tell a no-op from an edit by identity.
 */
export function applyFilterAction(
  filters: readonly FilterNode[],
  action: FilterAction,
  typeOf: FilterTypeOf,
): readonly FilterNode[] {
  switch (action.type) {
    case 'addFilter': {
      // The field may be a MEASURE: a filter applies to a source row, so a
      // measure with a per-row value is filtered exactly like a numeric
      // column. `typeOf` answers `null` for a ratio — which is why there is
      // nothing to add for one.
      const type = typeOf(action.dimensionId);
      if (!type) return filters;
      const taken = new Set(filters.map((node) => node.id));
      const rule = settleRule(
        {
          id: action.id && !taken.has(action.id) ? action.id : newFilterId(action.dimensionId),
          field: action.dimensionId,
          op: action.op ?? DEFAULT_OP[type],
          value: action.value,
          paused: false,
        },
        type,
      );
      return [...filters, rule];
    }

    case 'removeFilter': {
      const next = filters.filter((node) => node.id !== action.filterId);
      return next.length === filters.length ? filters : next;
    }

    case 'setFilterRule': {
      let changed = false;
      const next = filters.map((node) => {
        if (node.id !== action.filterId || isFilterGroup(node)) return node;
        const merged: FilterRule = { ...node, ...action.patch };
        const type = typeOf(merged.field);
        if (!type) return node;
        changed = true;
        return settleRule(merged, type);
      });
      return changed ? next : filters;
    }
  }
}
